import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Container,
  Button,
  Modal,
  Paper,
  Rating,
  Slider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  FilterList,
  KeyboardArrowDown,
  Add,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { fetchPlaces } from '../../services/CommonFunctions';
import PlaceCard from './PlaceCard';
import { CenteredFab } from '../../Components/Fab/CustomButton';
import COLORS from '../../Utils/colors';
import { fetchCategories } from '../../services/CommonApiCall';
import BottomModal from '../../Components/BottomModel';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import { useLocationForPlaces } from '../../hooks/useLocationForPlaces';

interface Place {
  _id: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  type: string;
  featured: boolean;
  averageRating: number;
  distance?: string;
  distanceValue?: number;
  location: {
    type: string;
    coordinates?: [number, number];
    lat?: number;
    lng?: number;
    zoom: number;
  };
  address: {
    street: string;
    city: string;
    state: string;
    country: string;
    zipcode: string;
  };
  owner_details: {
    name: string;
    email: string;
    phone: string;
  };
  files: Array<{
    fileURL: string;
    fileType: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface FilterValues {
  rating: number;
  distance: number;
}

const convertKmToMeters = (kilometers: number): number => kilometers * 1000;

interface FilterComponentProps {
  open: boolean;
  onClose: () => void;
  initialValues: FilterValues;
  onApply: (values: FilterValues) => void;
}

const FilterComponent = React.memo(
  ({ open, onClose, initialValues, onApply }: FilterComponentProps) => {
    const { rating: initialRating, distance: initialDistance } = initialValues;
    const [localRating, setLocalRating] = useState(initialRating);
    const [localDistance, setLocalDistance] = useState(initialDistance);

    useEffect(() => {
      if (open) {
        setLocalRating(initialRating);
        setLocalDistance(initialDistance);
      }
    }, [open, initialRating, initialDistance]);

    return (
      <BottomModal
        open={open}
        onClose={onClose}
        title="Filter"
        children={
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mx: 4 }}>
              <Typography>Rating</Typography>
              <Rating
                value={localRating}
                onChange={(_, newValue) => {
                  setLocalRating(newValue || 0);
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mx: 4 }}>
              <Typography>Distance</Typography>
              <Slider
                sx={{
                  '& .MuiSlider-thumb': {
                    color: COLORS.primary,
                    '&:hover': {
                      boxShadow: '0 0 0 8px rgba(0, 0, 0, 0.1)',
                    },
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: COLORS.primary,
                  },
                }}
                value={localDistance}
                onChange={(_, newValue) => {
                  setLocalDistance(newValue as number);
                }}
                min={0}
                max={5000}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} km (${convertKmToMeters(value)} m)`}
              />
              <Typography variant="caption" color="text.secondary">
                {localDistance} km
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                backgroundColor: COLORS.primary,
                color: 'white',
                borderRadius: 100,
                px: 4,
                py: 1,
                mx: 4,
                my: 6,
              }}
              onClick={() => onApply({ rating: localRating, distance: localDistance })}
            >
              Apply
            </Button>
          </>
        }
      />
    );
  }
);



const AllPlaces: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  
  const { places, currentPage, hasMore, loading } = useSelector((state: RootState) => state.places);
  const categories = useSelector((state: RootState) => state.category.categories);
  const { userLocation, isLocationLoading } = useLocationForPlaces();
  const routerLocation = useLocation();
  const incomingCategory = (routerLocation.state as { category?: string } | null)?.category;
  const incomingSortBy = (routerLocation.state as { sortBy?: string } | null)?.sortBy;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('nearby');
  const [isSortModalOpen, setIsSortModalOpen] = useState(false);
  const [filteredPlaces, setFilteredPlaces] = useState<Place[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<Place[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [placesWithDistance, setPlacesWithDistance] = useState<Place[]>([]);
  const isLoadingMore = useRef(false);
  const [pendingCategory, setPendingCategory] = useState<string>('all');
  
  // Filter states
  const [activeFilters, setActiveFilters] = useState<FilterValues>({
    rating: 0,
    distance: 5000,
  });
  
  // Pagination state for filtered results
  const [filteredPage, setFilteredPage] = useState(1);
  const [hasMoreFiltered, setHasMoreFiltered] = useState(true);
  const isLoadingMoreFiltered = useRef(false);
  
  // Pagination state for sortBy results
  const [sortByPage, setSortByPage] = useState(1);
  const [hasMoreSortBy, setHasMoreSortBy] = useState(true);
  const isLoadingMoreSortBy = useRef(false);
  
  // Check if filters are active (not default values)
  const isFilterActive = activeFilters.rating > 0 || activeFilters.distance < 5000;
  
  // Check if custom sortBy is selected (not default 'nearby')
  const isSortByActive = sortBy !== 'nearby';
  
  // Calculate maxInitialDisplay based on screen width: xs (3), sm (5), md+ (8)
  const maxInitialDisplay = isMd ? 8 : isXs ? 5 : 6;

  // Unified function to build all filter/sort options
  const buildAllOptions = useCallback((overrideSortBy?: string) => {
    const options: any = {};
    const sortByToUse = overrideSortBy !== undefined ? overrideSortBy : sortBy;
    const trimmed = searchTerm.trim();
    const searchActive = !!trimmed;

    // Add location if available (skip when search is active)
    if (!searchActive && userLocation?.lng && userLocation?.lat) {
      options.longitude = userLocation.lng;
      options.latitude = userLocation.lat;
    }

    // Add distance filter (only if less than max default)
    if (activeFilters.distance < 5000) {
      options.maxDistance = convertKmToMeters(activeFilters.distance);
    }

    // Add rating filter
    if (activeFilters.rating > 0) {
      options.minRating = activeFilters.rating;
    }

    // Add category if selected
    if (selectedCategory !== 'all') {
      options.category = selectedCategory;
    }

    // Add search if present
    if (trimmed) {
      options.search = trimmed;
    }

    // sortBy: empty when search is active, otherwise add only if not default 'nearby'
    if (searchActive) {
      options.sortBy = '';
    } else if (sortByToUse !== 'nearby') {
      options.sortBy = sortByToUse;
    }

    return options;
  }, [userLocation, activeFilters, selectedCategory, searchTerm, sortBy]);

  // Unified load more function that combines all filters
  const loadMorePlaces = useCallback(() => {
    // Check if we should load more based on active filters
    if (isFilterActive && (!hasMoreFiltered || isLoadingMoreFiltered.current)) return;
    if (isSortByActive && (!hasMoreSortBy || isLoadingMoreSortBy.current)) return;
    if (!isFilterActive && !isSortByActive && (!hasMore || isLoadingMore.current)) return;
    if (loading) return;

    // Use appropriate ref to prevent duplicate calls
    if (isFilterActive) {
      isLoadingMoreFiltered.current = true;
    } else if (isSortByActive) {
      isLoadingMoreSortBy.current = true;
    } else {
      isLoadingMore.current = true;
    }

    const allOptions = buildAllOptions();
    const nextPage = isFilterActive ? filteredPage + 1 : isSortByActive ? sortByPage + 1 : currentPage + 1;

    fetchPlaces(dispatch, nextPage, 5, true, allOptions)
      .then((placesData) => {
        if (isFilterActive) {
          setFilteredPage(prev => prev + 1);
          setHasMoreFiltered(placesData.length === 5);
        } else if (isSortByActive) {
          setSortByPage(prev => prev + 1);
          setHasMoreSortBy(placesData.length === 5);
        } else {
          // Regular pagination - hasMore is managed by Redux
        }
      })
      .catch((err) => {
        console.error('Load more error:', err.message);
        if (isFilterActive) setHasMoreFiltered(false);
        if (isSortByActive) setHasMoreSortBy(false);
      })
      .finally(() => {
        if (isFilterActive) {
          isLoadingMoreFiltered.current = false;
        } else if (isSortByActive) {
          isLoadingMoreSortBy.current = false;
        } else {
          isLoadingMore.current = false;
        }
      });
  }, [dispatch, hasMore, loading, isFilterActive, isSortByActive, hasMoreFiltered, hasMoreSortBy, filteredPage, sortByPage, currentPage, buildAllOptions]);


  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 1000
    ) {
      // Use unified load more that handles all combinations
      loadMorePlaces();
    }
  }, [loadMorePlaces]);



  // Same pattern as Post: wait for location when we need it, then fetch
  useEffect(() => {
    if (isLocationLoading) return;
    if (initialized || places.length > 0 || loading) return;
    setInitialized(true);
    const options = buildAllOptions();
    fetchPlaces(dispatch, 1, 5, false, options);
  }, [dispatch, initialized, places.length, loading, buildAllOptions, isLocationLoading]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };
    
    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  // Fetch categories only once
  useEffect(() => {
    fetchCategories(dispatch,'post-places');
  }, [dispatch]);

  useEffect(() => {
    if (isCategoryModalOpen) {
      setPendingCategory(selectedCategory);
    }
  }, [isCategoryModalOpen, selectedCategory]);


  // Unified function to handle both search and category filtering
  const applyFilters = useCallback((newCategory?: string) => {
    // Use newCategory if provided, otherwise use selectedCategory
    const categoryToUse = newCategory !== undefined ? newCategory : selectedCategory;
    if (categoryToUse !== selectedCategory) {
      setSelectedCategory(categoryToUse);
    }
    
    // Add search terms if any
    const trimmed = searchTerm.trim();
    
    // Build all options including filters, sortBy, category, search
    const allOptions = buildAllOptions();
    
    // If category changed, update it in options
    if (categoryToUse !== 'all') {
      allOptions.category = categoryToUse;
    } else {
      delete allOptions.category;
    }

    // Reset pagination
    setFilteredPage(1);
    setHasMoreFiltered(true);
    setSortByPage(1);
    setHasMoreSortBy(true);

    console.log('Unified filter API call with combined options:', allOptions);

    fetchPlaces(dispatch, 1, 5, false, allOptions)
      .then((placesData) => {
        if (trimmed) {
          // If there are search terms, use search results
          setSearchResults(placesData);
          setIsSearchActive(true);
        } else {
          // If no search terms, update places directly
          setIsSearchActive(false);
          setSearchResults([]);
        }
      })
      .catch((err) => {
        console.error('Filter error:', err.message);
        setSearchResults([]);
        setIsSearchActive(false);
      });
  }, [dispatch, searchTerm, selectedCategory, buildAllOptions]);

  useEffect(() => {
    if (!incomingCategory) {
      return;
    }

    if (incomingCategory === selectedCategory) {
      return;
    }

    setSelectedCategory(incomingCategory);
    setPendingCategory(incomingCategory);
    applyFilters(incomingCategory);
  }, [incomingCategory, selectedCategory, applyFilters]);

  // Handle incoming sortBy from navigation state
  useEffect(() => {
    if (!incomingSortBy) {
      return;
    }

    if (incomingSortBy === sortBy) {
      return;
    }

    setSortBy(incomingSortBy);
  }, [incomingSortBy, sortBy]);

  // Function to perform search by tags
  const handleSearch = () => {
    applyFilters();
  };

  // Recompute filteredPlaces whenever relevant data changes
  useEffect(() => {
    const sourcePlaces = isSearchActive ? searchResults : places;
    let filtered = [...sourcePlaces];

    // Filter by category (skip if 'all')
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(place =>
        place.categories.some((cat: string) =>
          cat.toLowerCase().includes(selectedCategory.toLowerCase())
        )
      );
    }

    // Only apply local sorting if sortBy is 'name' or 'nearby'
    // For 'rating' and 'newly_added', the API already handles sorting
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }
    // 'nearby' sorting is handled by distance calculation in placesWithDistance
    // 'rating' and 'newly_added' are sorted by the API

    setFilteredPlaces(filtered);
  }, [places, searchResults, isSearchActive, selectedCategory, sortBy]);

  // Calculate distances when user location and places are available
  useEffect(() => {
    const calculateDistances = async () => {
      if (!userLocation || filteredPlaces.length === 0) {
        setPlacesWithDistance(filteredPlaces);
        return;
      }

      try {
        const placesWithCalculatedDistance = await Promise.all(
          filteredPlaces.map(async (place) => {
            const placeLat =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[1]
                : place.location?.lat;
            const placeLng =
              place.location?.coordinates && place.location.coordinates.length >= 2
                ? place.location.coordinates[0]
                : place.location?.lng;

            if (placeLat == null || placeLng == null) {
              return {
                ...place,
                distance: 'N/A',
                distanceValue: Infinity,
              };
            }

            try {
              const distance = await calculateDistance(
                userLocation.lat,
                userLocation.lng,
                placeLat,
                placeLng
              );
              return {
                ...place,
                distance: `${distance.toFixed(1)}km`,
                distanceValue: distance,
              };
            } catch (error) {
              console.warn(`Error calculating distance for place ${place.name}:`, error);
              return {
                ...place,
                distance: 'N/A',
                distanceValue: Infinity,
              };
            }
          })
        );

        // Only sort by distance if sortBy is 'nearby'
        // Otherwise, preserve the API's sorting order
        let sortedPlaces = placesWithCalculatedDistance;
        if (sortBy === 'nearby') {
          sortedPlaces = placesWithCalculatedDistance.sort(
            (a, b) => (a.distanceValue || Infinity) - (b.distanceValue || Infinity)
          );
        }

        setPlacesWithDistance(sortedPlaces);
      } catch (error) {
        console.error('Error calculating distances:', error);
        setPlacesWithDistance(filteredPlaces);
      }
    };

    calculateDistances();
  }, [userLocation, filteredPlaces, sortBy]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCategoryChange = (category: string, closeModal = false) => {
    if (category === selectedCategory) {
      if (closeModal) {
        setIsCategoryModalOpen(false);
      }
      return;
    }

    setSelectedCategory(category);
    applyFilters(category);

    if (closeModal) {
      setIsCategoryModalOpen(false);
    }
  };

  const handleCategorySave = () => {
    handleCategoryChange(pendingCategory, true);
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography>Loading places...</Typography>
      </Container>
    );
  }

  const handleApplyFilters = ({ rating, distance }: FilterValues) => {
    setActiveFilters({ rating, distance });
    // Reset pagination state when applying new filters
    setFilteredPage(1);
    setHasMoreFiltered(true);
    
    // Build all options including filters, sortBy, category, search
    const allOptions = buildAllOptions();
    
    // Update with new filter values
    if (distance < 5000) {
      allOptions.maxDistance = convertKmToMeters(distance);
    } else {
      delete allOptions.maxDistance;
    }

    if (rating > 0) {
      allOptions.minRating = rating;
    } else {
      delete allOptions.minRating;
    }

    console.log('Apply filters with combined options:', allOptions);

    fetchPlaces(dispatch, 1, 5, false, allOptions)
      .catch((err) => { 
        console.error('Filter error:', err.message);
        setHasMoreFiltered(false);
      });
  };
  return (
    <Container maxWidth="sm" >
      {/* Add place button */}
        <CenteredFab
            icon={<Add />}
            label="Add place"
            onClick={() => navigate("/createplace")}
            position="end"
            sx={{
              borderRadius:'12px',
                backgroundColor: COLORS.primary,
                color: 'white',
                '&:hover': {
                    backgroundColor: COLORS.burntOrange,
                }
            }}
          />
          <FilterComponent
            open={isFilterModalOpen}
            onClose={() => setIsFilterModalOpen(false)}
            initialValues={activeFilters}
            onApply={(values) => {
              setIsFilterModalOpen(false);
              handleApplyFilters(values);
            }}
          />

      {/* Location Subtitle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}> 

      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '80%',
          px:'16px',
        }}
        >
        {userLocation?.name}
      </Typography>
      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <IconButton onClick={() => setIsFilterModalOpen(true)}>
            <FilterList />
          </IconButton>
        </Box>
        </Box>

      {/* Search Bar */}
      <Box sx={{ mx: '16px' }}>
      <TextField
        fullWidth
        placeholder="Start your search"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          if (e.target.value.trim() === '') {
            // Clear search state when input is cleared
            setIsSearchActive(false);
            setSearchResults([]);
          }
        }}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleSearch}>
                <Search color="action" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 100,
            backgroundColor: '#fff8f5',
            '& fieldset': {
              border: 'none',
            },
          },
        }}
        />
        </Box>

      {/* Categories */}
      <Box sx={{ mb: 2 ,mx:'16px'}}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1.5
        }}>
          <Typography variant="subtitle1" fontWeight="600">
            Categories
          </Typography>
          <Typography variant="subtitle1"  onClick={() => setIsCategoryModalOpen(true)}>
            View all
          </Typography>
          {/* <Button variant="contained" color="primary" onClick={() => navigate('/createplace')}>
            Add place
          </Button> */}
        
        </Box>
        
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 1,
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari and Opera
            },
          }}
        >
        <Chip
              label="All"
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
              sx={{
                borderRadius: 3,
                backgroundColor: selectedCategory === 'all' ? '#FEB380' : 'transparent',
                color: selectedCategory === 'all' ? '#ffffff' : 'inherit',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: selectedCategory === 'all'
                    ? '#febd8f'
                    : 'rgba(254, 179, 128, 0.15)',
                },
                '&.MuiChip-clickable:focus, &.MuiChip-clickable:active': {
                  backgroundColor: selectedCategory === 'all'
                    ? '#febd8f'
                    : 'rgba(254, 179, 128, 0.15)',
                  boxShadow: 'none',
                },
              }}
            />
          {categories.slice(0, maxInitialDisplay).map((category) => (
            <Chip
              key={category.name}
              label={category.name}
              onClick={() => handleCategoryChange(category.name)}
              variant={selectedCategory === category.name ? 'filled' : 'outlined'}
              sx={{
                borderRadius: 3,
                minWidth: 'fit-content',
                backgroundColor: selectedCategory === category.name ? '#FEB380' : 'transparent',
                color: 'black',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: selectedCategory === category.name 
                    ? '#febd8f' 
                    : 'rgba(254, 179, 128, 0.15)'
                },
                '&.MuiChip-clickable:focus, &.MuiChip-clickable:active': {
                  backgroundColor: selectedCategory === category.name
                    ? '#febd8f'
                    : 'rgba(254, 179, 128, 0.15)',
                  boxShadow: 'none',
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Sort By */}
      <Stack
        sx={{
          backgroundColor: '#fff8f5',
          px: 1,
          pb: 10,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          borderWidth: '1px',
          borderColor: '#eedcce',
          borderStyle: 'solid',
          mx: 0,
          width: '95%',
          minHeight: 'calc(100vh - 200px)',
        }}
      >

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
      }}>
        <Typography variant="body2" color="text.secondary">
          Sort by
        </Typography>
        <Box
          onClick={() => setIsSortModalOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: searchTerm.trim() ? 'text.secondary' : undefined,
            }}
          >
            {searchTerm.trim()
              ? '—'
              : sortBy === 'nearby'
                ? 'Near by'
                : sortBy === 'rating'
                  ? 'Customer Review'
                  : 'Newly Listed'}
          </Typography>
          <KeyboardArrowDown />
        </Box>
      </Box>

      <BottomModal
        open={isSortModalOpen}
        onClose={() => setIsSortModalOpen(false)}
        title="Sort by"
        maxHeight="auto"
        minHeight="auto"
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {[
              { value: 'nearby', label: 'Near by' },
              { value: 'rating', label: 'Customer Review' },
              { value: 'name', label: 'Newly Listed' },
            ].map((option) => (
              <Box
                key={option.value}
                onClick={() => {
                  const newSortBy = option.value === 'nearby' ? 'nearby' : option.value === 'rating' ? 'rating' : 'newly_added';
                  setSortBy(newSortBy);
                  setIsSortModalOpen(false);
                  
                  // Reset pagination state when sortBy changes
                  setSortByPage(1);
                  setHasMoreSortBy(true);
                  
                  // Build all options including filters, sortBy, category, search with new sortBy value
                  const allOptions = buildAllOptions(newSortBy);
                  
                  console.log('Apply sortBy with combined options:', allOptions);
                  
                  fetchPlaces(dispatch, 1, 5, false, allOptions)
                    .then(() => {
                      setIsSearchActive(false);
                      setSearchResults([]);
                    })
                    .catch((err) => {
                      console.error('Sort error:', err.message);
                      setHasMoreSortBy(false);
                    });
                }}
                sx={{
                  p: '2px',
                  color: sortBy === option.value ? '#FEB380' : 'black',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#FEB380',
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: sortBy === option.value ? 600 : 400,
                    color: sortBy === option.value ? '#FEB380' : 'text.secondary',
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </BottomModal>

      {/* Places List */}
      <Stack spacing={2}>
        {(placesWithDistance.length > 0 ? placesWithDistance : filteredPlaces).map((place) => {
          const imageUrl = place.files?.[0]?.fileURL || '';
          const distance = place.distance || 'N/A';
          const rating = place?.averageRating; // This would come from reviews/ratings
          
          
          return (
            <Box key={place._id}>
              <PlaceCard
                imageUrl={imageUrl}
                title={place.name}
                distance={distance}
                category={place.categories || ['Sights']}
                tags={place.tags.slice(0, 2)} // Show max 2 tags
                rating={rating}
                islistview={true}
                id={place._id}
                />
            </Box>
          );
        })}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        )}
        
        {((isFilterActive && !hasMoreFiltered) || (isSortByActive && !hasMoreSortBy) || (!isFilterActive && !isSortByActive && !hasMore)) && filteredPlaces.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No more places to load
            </Typography>
          </Box>
        )}
      </Stack>

      {filteredPlaces.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No places found matching your criteria
          </Typography>
        </Box>
      )}
      </Stack>

      {/* Category Modal */}
      <Modal
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 360,
            p: 3,
            borderRadius: '24px',
            boxShadow: '0px 24px 60px rgba(23, 30, 38, 0.18)',
            bgcolor: '#FFFFFF',
          }}
        >
          <Stack spacing={3}>
            <Box textAlign="center">
              <Typography variant="h6" fontWeight={600}>
                Categories
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                justifyContent: 'center',
              }}
            >
              <Chip
                label="All"
                onClick={() => setPendingCategory('all')}
                variant={pendingCategory === 'all' ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: 3,
                  backgroundColor: pendingCategory === 'all' ? '#FEB380' : 'transparent',
                  color: 'black',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: pendingCategory === 'all'
                      ? '#febd8f'
                      : 'rgba(254, 179, 128, 0.15)',
                  },
                  '&.MuiChip-clickable:focus, &.MuiChip-clickable:active': {
                    backgroundColor: pendingCategory === 'all'
                      ? '#febd8f'
                      : 'rgba(254, 179, 128, 0.15)',
                    boxShadow: 'none',
                  },

                }}
              />

              {categories.map((category) => (
                <Chip
                  key={`modal-${category.name}`}
                  label={category.name}
                  onClick={() => setPendingCategory(category.name)}
                  variant={pendingCategory === category.name ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: pendingCategory === category.name ? '#FEB380' : 'transparent',
                    color: pendingCategory === category.name ? '#ffffff' : 'black',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: pendingCategory === category.name
                        ? '#febd8f'
                        : 'rgba(254, 179, 128, 0.15)',
                    },
                    '&.MuiChip-clickable:focus, &.MuiChip-clickable:active': {
                      backgroundColor: pendingCategory === category.name
                        ? '#febd8f'
                        : 'rgba(254, 179, 128, 0.15)',
                      boxShadow: 'none',
                    },
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
            
              <Button
                variant="contained"
                sx={{
                  backgroundColor: COLORS.primary,
                  color: '#FFFFFF',
                  borderRadius: '999px',
                  px: 3,
                  py: 0.75,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: COLORS.burntOrange,
                  },
                }}
                onClick={handleCategorySave}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Modal>
    </Container>
  );
};

export default AllPlaces;
